
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function CommercialPanelUpgradesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Commercial Electrical Panel Upgrades',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional commercial electrical panel upgrade services in Fresno CA to increase capacity and meet modern business electrical demands',
    url: `${siteUrl}/services/commercial-specialist/commercial-panel-upgrades`,
  };

  const features = [
    {
      icon: 'ri-dashboard-3-line',
      title: 'Main Panel Upgrades',
      description: 'Upgrading main service panels to increase electrical capacity',
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Sub-Panel Installation',
      description: 'Adding sub-panels for expanded circuits and distribution',
    },
    {
      icon: 'ri-arrow-up-line',
      title: 'Service Upgrades',
      description: 'Increasing electrical service from 100A to 200A, 400A, or higher',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Code Compliance Upgrades',
      description: 'Bringing outdated panels up to current California electrical codes',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Three-Phase Panel Installation',
      description: 'Installing three-phase panels for heavy-duty commercial equipment',
    },
    {
      icon: 'ri-tools-line',
      title: 'Panel Replacement',
      description: 'Replacing obsolete or damaged commercial electrical panels',
    },
  ];

  return (
    <>
      <SEOHead
        title="Commercial Panel Upgrades Fresno | Electrical Service Upgrade CA"
        description="Expert commercial electrical panel upgrades in Fresno CA. Increase capacity, add circuits, and meet modern business electrical demands. Licensed C-10 contractors."
        canonical="/services/commercial-specialist/commercial-panel-upgrades"
        keywords="commercial panel upgrade Fresno, electrical service upgrade, business electrician, panel replacement"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Commercial Electrical Panel Upgrades — Fresno, CA"
        heroSubtitle="Increase electrical capacity and modernize your Fresno business electrical system"
        heroImage="https://public.readdy.ai/ai/img_res/fc33fde8de0fe7cc548b8955327503cf.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20large%20commercial%20electrical%20panel%20with%20heavy%20duty%20breakers%20and%20organized%20conduit%20runs%20in%20business%20facility&width=700&height=600&seq=comm-panel-fresno-001&orientation=portrait"
        fresnoContent="Upgrading Fresno commercial electrical systems to meet modern business demands. From downtown office buildings to retail centers, restaurants to industrial facilities throughout Fresno County, we provide professional electrical panel upgrades that increase capacity, improve safety, and support business growth. Our licensed electricians handle everything from small sub-panel additions to complete service upgrades and three-phase installations. We understand that businesses cannot afford extended downtime—we plan carefully and work efficiently to minimize disruption while delivering code-compliant electrical upgrades for Fresno commercial properties."
        features={features}
        faqs={[
          {
            question: 'When does a Fresno business need an electrical panel upgrade?',
            answer:
              "Fresno businesses need electrical panel upgrades when experiencing frequent breaker trips indicating overloaded circuits, planning to add new equipment or expand operations, installing high-demand equipment like HVAC or commercial kitchen appliances, experiencing flickering lights or voltage drops, dealing with outdated panels that don't meet current codes, adding electric vehicle charging stations, installing three-phase equipment, or when insurance companies require upgrades. Many older Fresno commercial buildings have 100-200 amp service that is inadequate for modern business operations. We evaluate your electrical system and recommend appropriate upgrades to support your business needs safely and reliably.",
          },
          {
            question: 'How much does a commercial panel upgrade cost in Fresno?',
            answer:
              'Commercial electrical panel upgrade costs in Fresno vary based on scope and existing conditions. Sub-panel additions typically range from $2,000-$5,000. Main panel replacements average $3,000-$8,000. Service upgrades from 100A to 200A cost $5,000-$12,000. Larger service upgrades to 400A or 600A range from $12,000-$30,000+. Three-phase service installation adds $8,000-$25,000+ depending on utility availability. Costs include panel equipment, installation labor, permits, inspections, and utility coordination. We provide detailed written estimates after evaluating your Fresno commercial property and electrical requirements. Proper panel upgrades prevent costly equipment damage and business downtime.',
          },
          {
            question: 'How long does a commercial panel upgrade take in Fresno?',
            answer:
              'Commercial panel upgrade timelines in Fresno depend on project scope. Simple sub-panel additions typically take 1-2 days. Main panel replacements average 2-4 days including permitting and inspections. Service upgrades requiring utility coordination take 2-4 weeks from start to finish. Three-phase service installation may require 4-8 weeks including PG&E coordination. Most panel work requires temporary power shutdown—we schedule this during off-hours or weekends to minimize business disruption. We coordinate all aspects including permits, utility shutdowns, inspections, and final connection to keep your Fresno business operational throughout the upgrade process.',
          },
          {
            question: 'Can you upgrade panels without shutting down our Fresno business?',
            answer:
              'We minimize business disruption during commercial panel upgrades in Fresno through careful planning. For sub-panel additions, we often work without shutting down main power. For main panel replacements or service upgrades, temporary power shutdown is required—we schedule this during evenings, weekends, or planned closure periods. For critical operations, we can install temporary power distribution to keep essential systems running during upgrades. We work efficiently to minimize downtime, typically completing power shutdowns within 4-8 hours. Our goal is to upgrade your electrical system with minimal impact on your Fresno business operations and revenue.',
          },
          {
            question: 'Do you handle permits and utility coordination for panel upgrades in Fresno?',
            answer:
              'Yes, we handle all permitting and utility coordination for commercial panel upgrades in Fresno CA. We pull electrical permits through the City of Fresno Building Department, coordinate power shutdowns with PG&E, schedule utility meter upgrades when required, ensure all work meets California electrical codes, coordinate inspections with city inspectors, and provide final approval documentation. For service upgrades, we manage the entire process with PG&E including applications, engineering reviews, and final connection. Our licensed C-10 contractors have extensive experience with commercial electrical upgrades and maintain excellent relationships with Fresno building inspectors and PG&E representatives.',
          },
          {
            question: 'Are you licensed for commercial panel upgrades in Fresno?',
            answer:
              "Yes, we hold a California C-10 Electrical Contractor license required for all commercial electrical panel work in Fresno CA. Our electricians are trained in commercial electrical systems, service calculations, panel installations, and California electrical codes. We carry full commercial liability insurance and workers' compensation coverage. All work is permitted through the City of Fresno Building Department and inspected for code compliance. We coordinate with PG&E for service upgrades and utility connections. Our team has extensive experience with commercial panel upgrades for offices, retail stores, restaurants, warehouses, and industrial facilities throughout Fresno and Central California.",
          },
        ]}
      />
    </>
  );
}
